/**
 * Frontend contract types for the assistant client tools (Epic 4, story 4.9).
 *
 * The three client tools — `navigate` / `highlight` / `startTour` — ride a `client_tool_call` run step
 * (the `clientTool` marker on `IAssistantRunStep`) and report their outcome through a single tool-result
 * POST. The tool-name identifiers, the step-marker/args shape, the result route/body, and the outcome
 * values are ASSUMPTIONS mirrored from the already-merged backend assistant epics (backend epic 3). They
 * are confined to this model + the executor service (each marked `BACKEND CONTRACT (assumption)`) and
 * enumerated in `BACKEND-CONTRACT.md`.
 *
 * This file is self-contained (it does NOT import `assistant.model.ts`); `assistant.model.ts` imports
 * `IClientToolCall` from here — one direction only, no import cycle.
 */

/**
 * BACKEND CONTRACT (assumption) — see BACKEND-CONTRACT.md
 * The client-tool registry identifiers. `executionSide: client`, `free` class (no per-invoke tier cost —
 * `free` does NOT relax route guards or the no-mutation ban). Only these three exist this story.
 */
export enum ClientToolName {
  Navigate = 'navigate',
  Highlight = 'highlight',
  StartTour = 'startTour',
}

/**
 * BACKEND CONTRACT (assumption) — see BACKEND-CONTRACT.md
 * The tool-result outcome values reported back to the run so it can advance.
 */
export enum ClientToolOutcome {
  // Reconciled 2026-08-12 against service-assistant: the posted outcome set is ok | blocked | failed.
  Success = 'ok',
  Blocked = 'blocked',
  Error = 'failed',
}

/**
 * BACKEND CONTRACT (assumption) — see BACKEND-CONTRACT.md
 * The args carried on a `client_tool_call` step. `target` is a router command (a URL string or a
 * `Router.navigate` commands array) for `navigate`; `anchor` is a `[data-tour-id]` value for `highlight`;
 * `tourId` is reserved for a future multi-tour `startTour` (today it always launches the guided walkthrough).
 */
export interface IClientToolArgs {
  target?: string | any[]
  anchor?: string
  tourId?: string
}

/**
 * BACKEND CONTRACT (assumption) — see BACKEND-CONTRACT.md
 * The `client_tool_call` marker on a run step: the tool to invoke plus its args.
 */
export interface IClientToolCall {
  name: ClientToolName
  args?: IClientToolArgs
}

/**
 * BACKEND CONTRACT (assumption) — see BACKEND-CONTRACT.md
 * The tool-result POST body: which conversation + step, and the outcome.
 */
export interface IClientToolResult {
  conversationId: string
  stepId: string
  outcome: ClientToolOutcome
}

/**
 * Pure accessor: returns the `client_tool_call` marker off a step, or `undefined` when the step is not a
 * client-tool step. Structural param (no dependency on `IAssistantRunStep`) to keep this file free of an
 * import cycle back to `assistant.model.ts`.
 */
export function getClientToolCall(step: { clientTool?: IClientToolCall }): IClientToolCall | undefined {
  return step.clientTool
}

/**
 * Pure normalizer: map a `navigate` target to a `Router.navigate` commands array. A string becomes a
 * single-segment command (`[string]`), an array passes through, `undefined` becomes `[]`.
 */
export function toNavigateCommands(target: string | any[] | undefined): any[] {
  if (target === undefined) {
    return []
  }
  return Array.isArray(target) ? target : [target]
}
